import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Search, Bell, MessageSquare, ChevronDown, Menu, X,
  Building2, Briefcase, Users, TrendingUp, MapPin,
  BookOpen, Calendar, ShieldCheck, LogOut, Settings,
  User, BarChart3, Plus
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import VerificationBadge from '../ui/VerificationBadge';
import './Navbar.css';

const NAV_LINKS = [
  {
    label: 'Network',
    icon: Users,
    items: [
      { label: 'My Connections',  href: '/connections',    icon: Users },
      { label: 'Find Professionals', href: '/search/people', icon: Search },
      { label: 'Groups',          href: '/groups',          icon: Users },
    ]
  },
  {
    label: 'Directory',
    icon: Building2,
    items: [
      { label: 'Company Directory', href: '/directory',          icon: Building2 },
      { label: 'Market Finder',     href: '/market-finder',      icon: MapPin },
      { label: 'Carriers',          href: '/directory?type=carrier', icon: ShieldCheck },
      { label: 'MGAs & MGUs',       href: '/directory?type=mga', icon: Building2 },
      { label: 'Agencies & Brokers', href: '/directory?type=agency', icon: Briefcase },
    ]
  },
  {
    label: 'Jobs',
    icon: Briefcase,
    href: '/jobs',
  },
  {
    label: 'Learn',
    icon: BookOpen,
    items: [
      { label: 'CE & Education',    href: '/education',    icon: BookOpen },
      { label: 'Events Calendar',   href: '/events',       icon: Calendar },
      { label: 'Publications',      href: '/publications', icon: BookOpen },
    ]
  },
  {
    label: 'Advertise',
    icon: BarChart3,
    href: '/advertise',
  },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery]   = useState('');
  const [openMenu, setOpenMenu]         = useState(null);
  const [mobileOpen, setMobileOpen]     = useState(false);
  const [profileOpen, setProfileOpen]   = useState(false);
  const [notifOpen, setNotifOpen]       = useState(false);
  const searchRef = useRef(null);

  // Close dropdowns on route change
  useEffect(() => {
    setOpenMenu(null);
    setProfileOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('.nav-dropdown-anchor') &&
          !e.target.closest('.dropdown-menu')) {
        setOpenMenu(null);
        setProfileOpen(false);
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const initials = user
    ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase()
    : '';

  return (
    <>
      <header className="navbar">
        <div className="navbar-inner">

          {/* Logo */}
          <Link to={user ? '/feed' : '/'} className="navbar-logo">
            <div className="navbar-logo-mark">EI</div>
            <span className="navbar-logo-text">
              Everything<strong>Insurance</strong>
            </span>
          </Link>

          {/* Search bar — only when logged in */}
          {user && (
            <form className="navbar-search" onSubmit={handleSearch}>
              <Search size={16} className="navbar-search-icon" />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search professionals, companies, markets…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="navbar-search-input"
              />
            </form>
          )}

          {/* Desktop Nav Links */}
          {user && (
            <nav className="navbar-nav">
              {NAV_LINKS.map(link => (
                <div key={link.label} className="nav-item">
                  {link.href ? (
                    <Link
                      to={link.href}
                      className={`nav-link ${location.pathname.startsWith(link.href) ? 'active' : ''}`}
                    >
                      <link.icon size={18} />
                      <span>{link.label}</span>
                    </Link>
                  ) : (
                    <button
                      className={`nav-link nav-dropdown-anchor ${openMenu === link.label ? 'active' : ''}`}
                      onClick={() => setOpenMenu(openMenu === link.label ? null : link.label)}
                    >
                      <link.icon size={18} />
                      <span>{link.label}</span>
                      <ChevronDown size={14} className={`nav-chevron ${openMenu === link.label ? 'open' : ''}`} />
                    </button>
                  )}

                  {/* Dropdown */}
                  {link.items && openMenu === link.label && (
                    <div className="dropdown-menu nav-dropdown">
                      {link.items.map(item => (
                        <Link key={item.href} to={item.href} className="dropdown-item">
                          <item.icon size={16} />
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>
          )}

          {/* Right side actions */}
          <div className="navbar-actions">
            {user ? (
              <>
                {/* Post button */}
                <button
                  className="btn btn-teal btn-sm navbar-post-btn"
                  onClick={() => navigate('/post/new')}
                >
                  <Plus size={15} />
                  Post
                </button>

                {/* Notifications */}
                <div className="nav-item">
                  <button
                    className={`nav-icon-btn nav-dropdown-anchor ${notifOpen ? 'active' : ''}`}
                    onClick={() => setNotifOpen(!notifOpen)}
                    aria-label="Notifications"
                  >
                    <Bell size={20} />
                    {user.unreadNotifications > 0 && (
                      <span className="nav-badge">{user.unreadNotifications}</span>
                    )}
                  </button>
                  {notifOpen && (
                    <div className="dropdown-menu nav-dropdown nav-notif-panel">
                      <div className="nav-panel-header">
                        <span className="font-600">Notifications</span>
                        <button className="btn btn-ghost btn-sm">Mark all read</button>
                      </div>
                      <div className="nav-panel-empty">
                        <Bell size={32} style={{ color: 'var(--gray-300)' }} />
                        <p>You're all caught up!</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Messages */}
                <button
                  className="nav-icon-btn"
                  onClick={() => navigate('/messages')}
                  aria-label="Messages"
                >
                  <MessageSquare size={20} />
                  {user.unreadMessages > 0 && (
                    <span className="nav-badge">{user.unreadMessages}</span>
                  )}
                </button>

                {/* Profile dropdown */}
                <div className="nav-item">
                  <button
                    className={`nav-avatar-btn nav-dropdown-anchor ${profileOpen ? 'active' : ''}`}
                    onClick={() => setProfileOpen(!profileOpen)}
                  >
                    {user.avatar ? (
                      <img src={user.avatar} alt={user.firstName} className="avatar avatar-sm" />
                    ) : (
                      <div className="avatar avatar-sm">{initials}</div>
                    )}
                    <ChevronDown size={14} className={`nav-chevron ${profileOpen ? 'open' : ''}`} />
                  </button>

                  {profileOpen && (
                    <div className="dropdown-menu nav-dropdown nav-profile-dropdown">
                      {/* Profile summary */}
                      <div className="nav-profile-summary">
                        {user.avatar ? (
                          <img src={user.avatar} alt="" className="avatar avatar-md" />
                        ) : (
                          <div className="avatar avatar-md">{initials}</div>
                        )}
                        <div>
                          <div className="font-600 text-dark">
                            {user.firstName} {user.lastName}
                            <VerificationBadge tier={user.verificationTier} size="sm" />
                          </div>
                          <div className="text-sm text-muted">{user.jobTitle}</div>
                          <div className="text-xs text-muted">{user.company?.name}</div>
                        </div>
                      </div>
                      <div className="dropdown-divider" />
                      <Link to={`/profile/${user.id}`} className="dropdown-item">
                        <User size={16} /> View Profile
                      </Link>
                      <Link to="/settings" className="dropdown-item">
                        <Settings size={16} /> Account Settings
                      </Link>
                      <Link to="/advertise" className="dropdown-item">
                        <BarChart3 size={16} /> Advertise on EI
                      </Link>
                      {user.companyAdmin && (
                        <Link to={`/company/${user.company?.id}/admin`} className="dropdown-item">
                          <Building2 size={16} /> Manage Company Page
                        </Link>
                      )}
                      <div className="dropdown-divider" />
                      <button className="dropdown-item text-red" onClick={handleLogout}>
                        <LogOut size={16} /> Sign Out
                      </button>
                    </div>
                  )}
                </div>

                {/* Mobile hamburger */}
                <button
                  className="nav-mobile-toggle"
                  onClick={() => setMobileOpen(!mobileOpen)}
                  aria-label="Menu"
                >
                  {mobileOpen ? <X size={22} /> : <Menu size={22} />}
                </button>
              </>
            ) : (
              /* Logged-out actions */
              <div className="navbar-auth-actions">
                <Link to="/login" className="btn btn-ghost">Sign In</Link>
                <Link to="/register" className="btn btn-teal">Join Free</Link>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile nav drawer */}
      {mobileOpen && user && (
        <div className="mobile-nav">
          <form className="mobile-search" onSubmit={handleSearch}>
            <Search size={16} />
            <input
              type="text"
              placeholder="Search…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </form>
          {NAV_LINKS.map(link => (
            <div key={link.label} className="mobile-nav-section">
              {link.href ? (
                <Link to={link.href} className="mobile-nav-link">
                  <link.icon size={18} /> {link.label}
                </Link>
              ) : (
                <>
                  <div className="mobile-nav-section-header">
                    <link.icon size={16} /> {link.label}
                  </div>
                  {link.items?.map(item => (
                    <Link key={item.href} to={item.href} className="mobile-nav-link mobile-nav-link-sub">
                      {item.label}
                    </Link>
                  ))}
                </>
              )}
            </div>
          ))}
          <div className="mobile-nav-footer">
            <button className="btn btn-outline w-full" onClick={handleLogout}>
              <LogOut size={16} /> Sign Out
            </button>
          </div>
        </div>
      )}
    </>
  );
}
